import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Shield, ArrowRight, Loader2, Mail, CheckCircle, ExternalLink, AlertTriangle, Scale, Sparkles, Check, MessageSquare, ChevronRight } from "lucide-react";

const OFFICIAL_LINKS = [
  { label: "RGPD (UE 2016/679)", url: "https://eur-lex.europa.eu/legal-content/FR/TXT/?uri=CELEX%3A32016R0679" },
  { label: "AI Act (UE 2024/1689)", url: "https://eur-lex.europa.eu/legal-content/FR/TXT/?uri=CELEX%3A32024R1689" },
  { label: "CNIL", url: "https://www.cnil.fr/" },
];

function NewsletterBox() {
  const [nlEmail, setNlEmail] = useState("");
  const [nlDone, setNlDone] = useState(false);
  const [nlLoading, setNlLoading] = useState(false);
  const handleNl = async (e) => {
    e.preventDefault();
    if (!nlEmail.trim()) return;
    setNlLoading(true);
    try { await API.post("/newsletter/subscribe", { email: nlEmail }); setNlDone(true); } catch {} finally { setNlLoading(false); }
  };
  if (nlDone) return (
    <div className="p-3 bg-white/5 rounded-md border border-white/10 flex items-center gap-2 text-emerald-400 text-sm">
      <CheckCircle className="h-4 w-4" /> Inscription confirmée !
    </div>
  );
  return (
    <div className="p-3 bg-white/5 rounded-md border border-white/10">
      <p className="text-[11px] text-slate-400 mb-2 flex items-center gap-1.5"><Mail className="h-3 w-3 text-[var(--ia-blue)]" /> Newsletter — Recevez nos analyses RGPD & AI Act</p>
      <form onSubmit={handleNl} className="flex gap-2">
        <input type="email" value={nlEmail} onChange={(e) => setNlEmail(e.target.value)} placeholder="votre@email.fr" required className="flex-1 px-3 py-1.5 rounded-md bg-white/10 border border-white/10 text-xs text-white placeholder:text-slate-500 focus:outline-none focus:border-[var(--ia-blue)]" data-testid="newsletter-email-input" />
        <button type="submit" disabled={nlLoading} className="px-3 py-1.5 rounded-md bg-[var(--ia-blue)] text-white text-xs font-medium hover:bg-[var(--ia-blue)]/90 disabled:opacity-50" data-testid="newsletter-subscribe-btn">{nlLoading ? "..." : "OK"}</button>
      </form>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}

// Mini-mockup de l'interface chat
function AppMockup() {
  return (
    <div className="rounded-lg border border-white/10 bg-white/5 overflow-hidden shadow-2xl shadow-black/20">
      {/* Toolbar */}
      <div className="h-7 bg-white/5 border-b border-white/10 flex items-center px-3 gap-1.5">
        <div className="w-2 h-2 rounded-full bg-red-400/60" /><div className="w-2 h-2 rounded-full bg-amber-400/60" /><div className="w-2 h-2 rounded-full bg-emerald-400/60" />
        <span className="text-[9px] text-slate-500 ml-2">ia-conforme.com</span>
      </div>
      <div className="p-4 space-y-3">
        {/* User message */}
        <div className="flex justify-end">
          <div className="bg-[var(--ia-blue)] text-white text-[10px] px-3 py-2 rounded-md max-w-[70%]">
            Mon chatbot client est-il conforme à l'AI Act ?
          </div>
        </div>
        {/* AI response */}
        <div className="flex justify-start">
          <div className="bg-white/10 border border-white/10 text-slate-200 text-[10px] px-3 py-2 rounded-md max-w-[80%] leading-relaxed">
            <p className="font-semibold text-emerald-400 mb-1">Risque Limité</p>
            Votre chatbot client relève du <strong>risque limité</strong> selon l'AI Act. Obligation principale : <strong>informer l'utilisateur</strong> qu'il interagit avec une IA...
            <span className="inline-block w-1.5 h-3 bg-emerald-400 ml-0.5 animate-pulse rounded-sm" />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [showLogin, setShowLogin] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [needsTotp, setNeedsTotp] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password, needsTotp ? totpCode : undefined);
    } catch (err) {
      const detail = err.response?.data?.detail || "";
      const headers = err.response?.headers || {};
      // Detect 2FA challenge: backend returns 401 with X-Auth-Step:totp
      if (headers["x-auth-step"] === "totp" || detail.toLowerCase().includes("2fa requis")) {
        setNeedsTotp(true);
        setError("");
      } else {
        setError(formatApiError(detail) || err.message);
      }
    } finally { setLoading(false); }
  };

  const handleGoogleLogin = () => {
    // OAuth Google direct : le backend gere le flux, pose les cookies puis redirige
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/google/login`;
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* ─── Left panel ─── */}
      <div className="hidden lg:flex lg:w-[55%] bg-[var(--ia-navy)] overflow-y-auto">
        <div className="max-w-xl mx-auto px-12 py-10 text-white flex flex-col justify-center">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-8">
            <Shield className="h-8 w-8 text-emerald-400" />
            <span className="text-xl font-bold" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>IA Conforme</span>
          </div>

          {/* Hero — accroche directe */}
          <h1 className="text-3xl sm:text-4xl font-bold mb-3 leading-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Ne laissez pas la réglementation IA vous freiner
          </h1>
          <p className="text-lg text-slate-300 leading-relaxed mb-8">
            Comprenez vos obligations en <span className="text-emerald-400 font-semibold">5 minutes</span>, pas en 5 heures.
          </p>

          {/* Mockup de l'app */}
          <div className="mb-8">
            <AppMockup />
          </div>

          {/* Services détaillés — sober regulatory tone */}
          <div className="mb-8 space-y-4" data-testid="services-detail">
            <h2 className="text-base font-semibold text-white border-l-2 border-emerald-400 pl-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Ce que vous obtenez
            </h2>

            <div className="grid grid-cols-1 gap-3">
              <div className="rounded-md border border-white/10 bg-white/[0.03] p-3">
                <div className="flex items-start gap-2.5">
                  <MessageSquare className="h-4 w-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-[13px] font-semibold text-white">Chat IA expert RGPD & AI Act</p>
                    <p className="text-[11px] text-slate-400 leading-relaxed mt-0.5">
                      Posez vos questions de conformité. L'IA cite les articles du RGPD (UE 2016/679) et de l'AI Act (UE 2024/1689) applicables, contextualise selon votre secteur, et propose des actions concrètes.
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-md border border-white/10 bg-white/[0.03] p-3">
                <div className="flex items-start gap-2.5">
                  <Scale className="h-4 w-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-[13px] font-semibold text-white">Simulateur de risque AI Act</p>
                    <p className="text-[11px] text-slate-400 leading-relaxed mt-0.5">
                      Décrivez votre cas d'usage IA — l'outil le classe selon les 4 niveaux de risque AI Act (inacceptable, haut risque, risque limité, risque minimal) et liste les obligations applicables.
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-md border border-white/10 bg-white/[0.03] p-3">
                <div className="flex items-start gap-2.5">
                  <Sparkles className="h-4 w-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-[13px] font-semibold text-white">Diagnostic Express (5 questions)</p>
                    <p className="text-[11px] text-slate-400 leading-relaxed mt-0.5">
                      En 2 minutes, évaluez votre maturité de conformité IA + RGPD : score global, points faibles identifiés et recommandations priorisées. Idéal avant un audit ou une certification.
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-md border border-white/10 bg-white/[0.03] p-3">
                <div className="flex items-start gap-2.5">
                  <CheckCircle className="h-4 w-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-[13px] font-semibold text-white">Checklist 16 points + Quiz</p>
                    <p className="text-[11px] text-slate-400 leading-relaxed mt-0.5">
                      Checklist actionnable avec suivi de progression, et quiz interactif pour valider vos connaissances RGPD/AI Act. Les Premium peuvent sauvegarder leur avancement.
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-md border border-white/10 bg-white/[0.03] p-3">
                <div className="flex items-start gap-2.5">
                  <ChevronRight className="h-4 w-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-[13px] font-semibold text-white">Glossaire, Timeline AI Act & Études de cas</p>
                    <p className="text-[11px] text-slate-400 leading-relaxed mt-0.5">
                      15 termes clés expliqués simplement, calendrier d'application AI Act 2024-2027, et études de cas par secteur (banque, santé, RH, marketing).
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Avantages gratuits */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-2 mb-8">
            <p className="text-[12px] text-slate-300 flex items-center gap-2"><Check className="h-3.5 w-3.5 text-emerald-400 flex-shrink-0" />3 conversations IA / jour</p>
            <p className="text-[12px] text-slate-300 flex items-center gap-2"><Check className="h-3.5 w-3.5 text-emerald-400 flex-shrink-0" />Glossaire & timeline complets</p>
            <p className="text-[12px] text-slate-300 flex items-center gap-2"><Check className="h-3.5 w-3.5 text-emerald-400 flex-shrink-0" />Simulateur & diagnostic express</p>
            <p className="text-[12px] text-slate-300 flex items-center gap-2"><Check className="h-3.5 w-3.5 text-emerald-400 flex-shrink-0" />Quiz & checklist de conformité</p>
          </div>

          {/* Disclaimer */}
          <div className="flex items-start gap-2 p-3 rounded-md bg-amber-500/10 border border-amber-500/15 mb-6">
            <AlertTriangle className="h-3.5 w-3.5 text-amber-400 mt-0.5 flex-shrink-0" />
            <p className="text-[10px] text-amber-200/80 leading-relaxed">
              Outil <strong>pédagogique</strong>. Ne constitue pas un conseil juridique — ne remplace pas un avocat, DPO ou juriste.
            </p>
          </div>

          {/* Official links + newsletter */}
          <div className="flex items-center gap-4 mb-4">
            {OFFICIAL_LINKS.map((link, i) => (
              <a key={i} href={link.url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-slate-400 hover:text-[var(--ia-blue)] transition-colors flex items-center gap-1" data-testid={`official-link-${i}`}>
                <ExternalLink className="h-2.5 w-2.5" />{link.label}
              </a>
            ))}
          </div>
          <NewsletterBox />
          <div className="mt-4 text-[9px] text-slate-500 text-center">© 3H33 SARL — Conseil, formation et intégration en IA</div>
        </div>
      </div>

      {/* ─── Right panel — CTA first, login second ─── */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8 bg-[var(--ia-surface)]">
        <div className="w-full max-w-md animate-fade-in">

          {/* Mobile logo */}
          <div className="flex justify-center lg:hidden mb-6">
            <div className="flex items-center gap-2">
              <Shield className="h-7 w-7 text-emerald-500" />
              <span className="text-xl font-bold text-[var(--ia-navy)]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>IA Conforme</span>
            </div>
          </div>

          {!showLogin ? (
            /* ─── Default : inscription CTA ─── */
            <div className="text-center">
              <h2 className="text-2xl sm:text-3xl font-bold text-[var(--ia-text)] mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                Essayez gratuitement
              </h2>
              <p className="text-[var(--ia-text-muted)] mb-2">
                Aucune carte bancaire requise — accès immédiat
              </p>
              <p className="text-xs text-slate-500 mb-8 max-w-sm mx-auto">
                3 conversations IA + 1 simulation + 1 diagnostic + glossaire complet, par jour, sans engagement.
              </p>

              {/* Big CTA buttons */}
              <div className="space-y-3 mb-6">
                <Button className="w-full h-12 text-base bg-emerald-600 hover:bg-emerald-700 shadow-lg shadow-emerald-600/20" onClick={() => navigate("/register")} data-testid="cta-register-btn">
                  <Sparkles className="h-5 w-5 mr-2" />
                  Créer mon compte gratuit
                </Button>

                <Button variant="outline" className="w-full h-12 text-base" onClick={handleGoogleLogin} data-testid="google-login-btn">
                  <GoogleIcon />
                  <span className="ml-2">S'inscrire avec Google</span>
                </Button>
              </div>

              {/* Benefits compact */}
              <div className="grid grid-cols-2 gap-2 mb-8 text-left">
                <div className="flex items-center gap-2 text-xs text-slate-600"><Check className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />Chat IA expert RGPD</div>
                <div className="flex items-center gap-2 text-xs text-slate-600"><Check className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />Simulateur de risques</div>
                <div className="flex items-center gap-2 text-xs text-slate-600"><Check className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />Diagnostic express</div>
                <div className="flex items-center gap-2 text-xs text-slate-600"><Check className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />Glossaire & quiz</div>
              </div>

              {/* Switch to login */}
              <Separator className="mb-4" />
              <p className="text-sm text-[var(--ia-text-muted)]">
                Déjà inscrit ?{" "}
                <button onClick={() => setShowLogin(true)} className="text-[var(--ia-blue)] hover:underline font-medium" data-testid="switch-to-login-btn">
                  Se connecter
                </button>
              </p>
            </div>
          ) : (
            /* ─── Login form ─── */
            <div>
              <h2 className="text-2xl font-bold text-[var(--ia-text)] text-center mb-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Connexion</h2>
              <p className="text-[var(--ia-text-muted)] text-center text-sm mb-6">Accédez à votre espace IA Conforme</p>

              <Button variant="outline" className="w-full mb-4 h-11" onClick={handleGoogleLogin} data-testid="google-login-form-btn">
                <GoogleIcon /><span className="ml-2">Continuer avec Google</span>
              </Button>

              <div className="relative mb-4">
                <Separator />
                <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-[var(--ia-surface)] px-3 text-xs text-[var(--ia-muted)]">ou</span>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className={`p-3 rounded-md text-sm border ${error.includes("créer un compte") ? "bg-blue-50 text-blue-700 border-blue-200" : error.includes("connexion Google") ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-red-50 text-red-700 border-red-200"}`} data-testid="login-error">
                    {error}
                    {error.includes("créer un compte") && (
                      <Link to="/register" className="block mt-1.5 font-medium underline">Créer un compte gratuitement</Link>
                    )}
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input data-testid="login-email-input" id="email" type="email" placeholder="votre@email.fr" value={email} onChange={(e) => setEmail(e.target.value)} required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Mot de passe</Label>
                  <Input data-testid="login-password-input" id="password" type="password" placeholder="Votre mot de passe" value={password} onChange={(e) => setPassword(e.target.value)} required />
                </div>
                {needsTotp && (
                  <div className="space-y-2 animate-fade-in">
                    <Label htmlFor="totp">Code 2FA</Label>
                    <Input
                      data-testid="login-totp-input"
                      id="totp"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      placeholder="123456"
                      value={totpCode}
                      onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      autoFocus
                      required
                      className="font-mono text-center text-lg tracking-widest"
                    />
                    <p className="text-xs text-[var(--ia-text-muted)]">Entrez le code à 6 chiffres de votre application d'authentification.</p>
                  </div>
                )}
                <Button data-testid="login-submit-button" type="submit" className="w-full bg-[var(--ia-navy)] hover:bg-[var(--ia-navy)]/90" disabled={loading}>
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ArrowRight className="h-4 w-4 mr-2" />}
                  Se connecter
                </Button>
              </form>

              <Separator className="my-4" />
              <p className="text-sm text-[var(--ia-text-muted)] text-center">
                Pas encore de compte ?{" "}
                <button onClick={() => setShowLogin(false)} className="text-emerald-600 hover:underline font-medium" data-testid="switch-to-register-btn">
                  Créer un compte gratuit
                </button>
              </p>
            </div>
          )}

          {/* Legal links */}
          <div className="mt-6 flex items-center justify-center gap-3 text-[10px] text-[var(--ia-muted)]">
            <a href="/legal?section=mentions" className="hover:underline">Mentions légales</a>
            <span>·</span>
            <a href="/legal?section=cgu" className="hover:underline">CGU</a>
            <span>·</span>
            <a href="/legal?section=confidentialite" className="hover:underline">Confidentialité</a>
          </div>

          {/* Mobile extras */}
          <div className="lg:hidden mt-4 space-y-2">
            <div className="p-2.5 rounded-md bg-emerald-50 border border-emerald-200">
              <p className="text-[10px] text-emerald-800 flex items-center gap-1.5"><Sparkles className="h-3 w-3 text-emerald-600" /><strong>Gratuit</strong> — aucune carte bancaire requise</p>
            </div>
            <div className="p-2.5 rounded-md bg-amber-50 border border-amber-200">
              <p className="text-[10px] text-amber-700 flex items-center gap-1.5"><AlertTriangle className="h-3 w-3 text-amber-500" />Outil pédagogique — ne remplace pas un avis juridique</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
